import { jsPDF } from 'jspdf';
import { autoTable } from 'jspdf-autotable';

export interface RequestPdfRow {
  issue_id: number;
  category_name?: string;
  block?: string;
  floor_name?: string;
  floor_unit?: string;
  checklist_location?: string;
  description?: string;
  status_name?: string;
  assigned_to_name?: string;
  approved?: string;
  account_name?: string;
  issuePhotos?: string[];
}

export interface RequestPdfExportOptions {
  requests: RequestPdfRow[];
  storageUrl: string;
  preparedBy: string;
  reportDate?: Date;
  fileName?: string;
}

interface FlatGroup {
  block?: string;
  floor_unit?: string;
  account_name?: string;
  requests: RequestPdfRow[];
}

const IMAGE_EXT = /\.(jpg|jpeg|png|gif|webp|bmp)$/i;
const PHOTO_COL_INDEX = 7;
const PAGE_MARGIN = 14;

function getFirstImagePath(photos?: string[]): string | null {
  if (!photos?.length) {
    return null;
  }
  return photos.find((path) => IMAGE_EXT.test(path.trim())) ?? null;
}

function buildImageUrl(storageUrl: string, path: string): string {
  const base = storageUrl.endsWith('/') ? storageUrl.slice(0, -1) : storageUrl;
  const cleanPath = path.startsWith('/') ? path.slice(1) : path;
  return `${base}/${cleanPath}`;
}

function resolveFetchableUrl(storageUrl: string, path: string): string {
  const absoluteUrl = buildImageUrl(storageUrl, path);

  if (typeof window === 'undefined') {
    return absoluteUrl;
  }

  const host = window.location.hostname;
  if (host === 'localhost' || host === '127.0.0.1') {
    try {
      return new URL(absoluteUrl).pathname;
    } catch {
      const cleanPath = path.startsWith('/') ? path.slice(1) : path;
      return `/api/storage/app/${cleanPath}`;
    }
  }

  return absoluteUrl;
}

function inferImageFormat(url: string): 'JPEG' | 'PNG' {
  return /\.png$/i.test(url) ? 'PNG' : 'JPEG';
}

function arrayBufferToDataUrl(
  buffer: ArrayBuffer,
  format: 'JPEG' | 'PNG'
): Promise<string> {
  const mime = format === 'PNG' ? 'image/png' : 'image/jpeg';
  const blob = new Blob([buffer], { type: mime });
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

async function loadImageViaFetch(
  url: string
): Promise<{ data: string; format: 'JPEG' | 'PNG' } | null> {
  try {
    const response = await fetch(url, { credentials: 'same-origin' });
    if (!response.ok) {
      return null;
    }

    const buffer = await response.arrayBuffer();
    if (!buffer.byteLength) {
      return null;
    }

    const format = inferImageFormat(url);
    return {
      data: await arrayBufferToDataUrl(buffer, format),
      format,
    };
  } catch {
    return null;
  }
}

function loadImageViaCanvas(
  url: string
): Promise<{ data: string; format: 'JPEG' | 'PNG' } | null> {
  return new Promise((resolve) => {
    const image = new Image();
    image.crossOrigin = 'anonymous';

    image.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = image.naturalWidth;
        canvas.height = image.naturalHeight;
        const context = canvas.getContext('2d');
        if (!context) {
          resolve(null);
          return;
        }

        context.drawImage(image, 0, 0);
        const format = inferImageFormat(url);
        const mime = format === 'PNG' ? 'image/png' : 'image/jpeg';
        resolve({
          data: canvas.toDataURL(mime, 0.92),
          format,
        });
      } catch {
        resolve(null);
      }
    };

    image.onerror = () => resolve(null);
    image.src = url;
  });
}

async function loadImageAsBase64(
  storageUrl: string,
  path: string
): Promise<{ data: string; format: 'JPEG' | 'PNG' } | null> {
  const fetchUrl = resolveFetchableUrl(storageUrl, path);
  const canvasUrl = buildImageUrl(storageUrl, path);

  const fetched = await loadImageViaFetch(fetchUrl);
  if (fetched) {
    return fetched;
  }

  if (fetchUrl !== canvasUrl) {
    const proxiedCanvas = await loadImageViaCanvas(fetchUrl);
    if (proxiedCanvas) {
      return proxiedCanvas;
    }
  }

  return loadImageViaCanvas(canvasUrl);
}

function formatReportDate(date: Date): string {
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
}

function groupRequestsByFlat(requests: RequestPdfRow[]): FlatGroup[] {
  const groups = new Map<string, FlatGroup>();

  for (const request of requests) {
    const key = `${request.block ?? ''}|${request.floor_unit ?? ''}`;
    const existing = groups.get(key);
    if (existing) {
      existing.requests.push(request);
      continue;
    }

    groups.set(key, {
      block: request.block,
      floor_unit: request.floor_unit,
      account_name: request.account_name,
      requests: [request],
    });
  }

  return Array.from(groups.values()).sort((a, b) => {
    const blockCompare = (a.block ?? '').localeCompare(b.block ?? '');
    if (blockCompare !== 0) {
      return blockCompare;
    }
    return (a.floor_unit ?? '').localeCompare(b.floor_unit ?? '', undefined, {
      numeric: true,
    });
  });
}

function buildFlatTitle(group: FlatGroup): string {
  return [
    group.account_name,
    group.block,
    group.floor_unit ? `FLAT NO - ${group.floor_unit}` : '',
  ]
    .filter(Boolean)
    .join(' ');
}

function drawFlatSectionHeader(
  doc: jsPDF,
  title: string,
  preparedBy: string,
  reportDate: Date,
  startY: number
): number {
  const pageWidth = doc.internal.pageSize.getWidth();
  const titleWidth = pageWidth - PAGE_MARGIN * 2;

  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  const titleLines = doc.splitTextToSize(title, titleWidth - 10);
  const titleBoxHeight = 8 + titleLines.length * 6;
  doc.rect(PAGE_MARGIN, startY, titleWidth, titleBoxHeight);
  doc.text(titleLines, pageWidth / 2, startY + 6, { align: 'center' });

  const metaY = startY + titleBoxHeight + 6;
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text(`Prepared By : ${preparedBy}`, PAGE_MARGIN, metaY);
  doc.text(
    `Inspection Date : ${formatReportDate(reportDate)}`,
    pageWidth - PAGE_MARGIN,
    metaY,
    { align: 'right' }
  );

  return metaY + 4;
}

function drawFlatTable(
  doc: jsPDF,
  flatRequests: RequestPdfRow[],
  imageCache: Map<number, { data: string; format: 'JPEG' | 'PNG' }>,
  startY: number
): void {
  const head = [[
    'SR',
    'Category',
    'Location',
    'Description',
    'Status',
    'Assigned To',
    'Approved',
    'Photo',
  ]];

  const body = flatRequests.map((request, index) => [
    String(index + 1),
    request.category_name || '-',
    request.checklist_location || '-',
    request.description || '-',
    request.status_name || '-',
    request.assigned_to_name || '-',
    request.approved || '-',
    { content: '', issueId: request.issue_id },
  ]);

  autoTable(doc, {
    startY,
    head,
    body,
    theme: 'grid',
    styles: {
      fontSize: 8,
      cellPadding: 2,
      valign: 'middle',
      overflow: 'linebreak',
      lineWidth: 0.2,
      lineColor: [0, 0, 0],
    },
    headStyles: {
      fillColor: [255, 255, 255],
      textColor: [0, 0, 0],
      fontStyle: 'bold',
      halign: 'center',
    },
    bodyStyles: {
      halign: 'center',
      minCellHeight: 30,
    },
    columnStyles: {
      0: { cellWidth: 10 },
      1: { cellWidth: 24 },
      2: { cellWidth: 30 },
      3: { halign: 'left' },
      4: { cellWidth: 18 },
      5: { cellWidth: 26 },
      6: { cellWidth: 20 },
      7: { cellWidth: 32, minCellHeight: 30 },
    },
    didParseCell: (data) => {
      if (data.section === 'body' && data.column.index === PHOTO_COL_INDEX) {
        data.cell.text = [];
      }
    },
    didDrawCell: (data) => {
      if (data.section !== 'body' || data.column.index !== PHOTO_COL_INDEX) {
        return;
      }

      const cellRaw = data.cell.raw as { issueId?: number } | string | number | null;
      const issueId =
        typeof cellRaw === 'object' && cellRaw !== null && 'issueId' in cellRaw
          ? cellRaw.issueId
          : undefined;
      if (issueId == null) {
        return;
      }

      const image = imageCache.get(issueId);
      if (!image) {
        return;
      }

      const padding = 2;
      const maxW = data.cell.width - padding * 2;
      const maxH = data.cell.height - padding * 2;
      const size = Math.min(maxW, maxH, 26);
      const x = data.cell.x + (data.cell.width - size) / 2;
      const y = data.cell.y + (data.cell.height - size) / 2;
      doc.addImage(image.data, image.format, x, y, size, size);
    },
  });
}

export async function exportRequestsToPdf(
  options: RequestPdfExportOptions
): Promise<void> {
  const {
    requests,
    storageUrl,
    preparedBy,
    reportDate = new Date(),
    fileName,
  } = options;

  if (!requests.length) {
    return;
  }

  const flatGroups = groupRequestsByFlat(requests);
  const imageCache = new Map<number, { data: string; format: 'JPEG' | 'PNG' }>();

  await Promise.all(
    requests.map(async (request) => {
      const imagePath = getFirstImagePath(request.issuePhotos);
      if (!imagePath) {
        return;
      }

      const image = await loadImageAsBase64(storageUrl, imagePath);
      if (image) {
        imageCache.set(request.issue_id, image);
      }
    })
  );

  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });

  flatGroups.forEach((group, groupIndex) => {
    if (groupIndex > 0) {
      doc.addPage();
    }

    const tableStartY = drawFlatSectionHeader(
      doc,
      buildFlatTitle(group),
      preparedBy,
      reportDate,
      PAGE_MARGIN
    );

    drawFlatTable(doc, group.requests, imageCache, tableStartY);
  });

  let exportFileName = fileName;
  if (!exportFileName) {
    const sanitizeSegment = (name: string): string => {
      return name
        .trim()
        .replace(/[^a-zA-Z0-9]/g, '_')
        .replace(/_+/g, '_')
        .replace(/^_+|_+$/g, '');
    };

    const projectNames: string[] = [];
    const wingNames: string[] = [];
    const floorNames: string[] = [];
    const unitNames: string[] = [];

    requests.forEach((req) => {
      if (req.account_name && req.account_name.trim()) {
        const pTrimmed = req.account_name.trim();
        if (!projectNames.includes(pTrimmed)) {
          projectNames.push(pTrimmed);
        }
      }
      if (req.block && req.block.trim()) {
        const wTrimmed = req.block.trim();
        if (!wingNames.includes(wTrimmed)) {
          wingNames.push(wTrimmed);
        }
      }
      const fName = req.floor_name || (req as any).floor;
      if (fName && String(fName).trim()) {
        const fTrimmed = String(fName).trim();
        if (!floorNames.includes(fTrimmed)) {
          floorNames.push(fTrimmed);
        }
      }
      if (req.floor_unit && req.floor_unit.trim()) {
        const uTrimmed = req.floor_unit.trim();
        if (!unitNames.includes(uTrimmed)) {
          unitNames.push(uTrimmed);
        }
      }
    });

    const formattedNames = projectNames
      .map(sanitizeSegment)
      .filter(Boolean);

    const nameParts: string[] = [];
    if (formattedNames.length > 0) {
      nameParts.push(formattedNames.join('_'));
    } else {
      nameParts.push(`requests-report-${formatReportDate(reportDate)}`);
    }

    if (wingNames.length === 1) {
      const sWing = sanitizeSegment(wingNames[0]);
      if (sWing) {
        nameParts.push(sWing);
      }
    }
    if (floorNames.length === 1) {
      const sFloor = sanitizeSegment(floorNames[0]);
      if (sFloor) {
        nameParts.push(sFloor);
      }
    }
    if (unitNames.length === 1) {
      const sUnit = sanitizeSegment(unitNames[0]);
      if (sUnit) {
        nameParts.push(sUnit);
      }
    }

    exportFileName = `${nameParts.join('_')}.pdf`;
  }

  doc.save(exportFileName);
}
